/**
 * Manage recently visited documentation pages/sections
 *
 * Features:
 * - Backend-first: always attempt backend operations first
 * - localStorage fallback: use local storage when backend fails
 * - Immediate UI Updates: Update localStorage immediately for instant UX.
 *    Backend sync happens in background
 * - Backend failures are caught and logged (development only)
 * - UI never breaks due to backend unavailability.
 *    Users get consistent experience even if backend is down
 * - Removals are immediate (localStorage update)
 */

/* eslint-disable prefer-promise-reject-errors */
/* eslint-disable max-classes-per-file */
import { getBEDEV2ServiceBasePath } from '@modules/utilities/constants/baseUrl';
import { Configuration } from '@rbx/clients';
import safeLocalStorage from '@modules/utilities/utils/safeLocalStorage';
import { TSearchListItem } from '@modules/ui/search/types/SearchListItem';
import { getDateDaysAgoTime } from '@modules/miscellaneous/common/utilities/dateUtils';

const CreatorHistoryRecentlyVisitedStorageKey = 'CreatorDocumentation.RecentlyVisited';

export interface CreatorHistoryConfig {
  maxItems: number;
  maxStoredItems: number;
  maxDaysAgo: number;
}

const isDevelopment = process.env.environment !== 'production';

const DEFAULT_API_CONFIG: CreatorHistoryConfig = {
  maxItems: 5,
  maxStoredItems: 100,
  maxDaysAgo: 30,
};

// TODO: replace with actual API.
//   Temporary implementation while real backend API is being developed
class PlaceholderCreatorHistoryApi {
  private config: Configuration;

  private apiConfig: CreatorHistoryConfig;

  private placeholderMethod: () => Promise<TSearchListItem[]>;

  constructor(config: Configuration, apiConfig: Partial<CreatorHistoryConfig> = {}) {
    this.config = config;
    this.apiConfig = {
      ...DEFAULT_API_CONFIG,
      ...apiConfig,
    };
    this.placeholderMethod = () => Promise.resolve([]);
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
  getRecentlyVisited = async (getAllItems?: boolean): Promise<TSearchListItem[]> => {
    return Promise.reject('Not yet implemented');
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars, class-methods-use-this
  addToRecentlyVisited = async (item: TSearchListItem): Promise<void> => {
    return Promise.reject('Not yet implemented');
  };

  // eslint-disable-next-line class-methods-use-this
  removeFromRecentlyVisited = async (
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    itemId: TSearchListItem['id'],
  ): Promise<TSearchListItem[]> => {
    return Promise.reject('Not yet implemented');
  };
}

/**
 * Track and update Recently Visited Items (pages or API reference sections, etc) for a creator.
 * Fall back to local storage if backend fails.
 */
class CreatorHistoryClient {
  private config: CreatorHistoryConfig;

  private maxDaysAgoTimestamp: number;

  private creatorHistoryApi: PlaceholderCreatorHistoryApi;

  constructor(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    basePath: string = getBEDEV2ServiceBasePath('creator-history'), // TODO
    config: Partial<CreatorHistoryConfig> = {},
  ) {
    this.config = {
      ...DEFAULT_API_CONFIG,
      ...config,
    };
    this.maxDaysAgoTimestamp = getDateDaysAgoTime(this.config.maxDaysAgo);

    const defaultConfig = new Configuration({
      basePath,
      credentials: 'include',
    });
    this.creatorHistoryApi = new PlaceholderCreatorHistoryApi(defaultConfig, this.config);
  }

  async getRecentlyVisited(getAllItems?: boolean): Promise<TSearchListItem[]> {
    try {
      return await this.creatorHistoryApi.getRecentlyVisited(getAllItems);
    } catch (error) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error('Failed to get recently visited items:', error);
      }
      return this.getRecentlyVisitedFromLocalStorage(getAllItems);
    }
  }

  private async addToRecentlyVisitedToLocalStorage(item: TSearchListItem): Promise<void> {
    const currentItems = await this.getRecentlyVisited(true);
    const filteredItems = currentItems.filter((existing) => existing.id !== item.id);
    const updated = [item, ...filteredItems].slice(0, this.config.maxStoredItems);
    safeLocalStorage.setItem(CreatorHistoryRecentlyVisitedStorageKey, JSON.stringify(updated));
  }

  async addToRecentlyVisited(item: TSearchListItem): Promise<void> {
    if (!item.path) return;
    const path = item.ignoreHash ? item.path.replace(/#.*$/, '') : item.path;
    const convertedItem = {
      ...item,
      id: path,
      path,
    };
    await this.addToRecentlyVisitedToLocalStorage(convertedItem);
    try {
      await this.creatorHistoryApi.addToRecentlyVisited(convertedItem);
    } catch (error) {
      if (isDevelopment) {
        // eslint-disable-next-line no-console
        console.error('Failed to log recently visited to backend:', error);
      }
    }
  }

  private getRecentlyVisitedFromLocalStorage(getAllItems?: boolean): TSearchListItem[] {
    try {
      const stored = safeLocalStorage.getItem(CreatorHistoryRecentlyVisitedStorageKey);
      if (!stored) return [];

      const parsed = JSON.parse(stored);
      if (!Array.isArray(parsed)) {
        throw new Error(`Invalid stored recently visited data: ${JSON.stringify(parsed)}`);
      }
      return parsed
        .filter(
          (item) =>
            item.accessedAt && new Date(item.accessedAt).getTime() >= this.maxDaysAgoTimestamp,
        )
        .slice(0, getAllItems ? undefined : this.config.maxItems);
    } catch (error) {
      if (isDevelopment)
        // eslint-disable-next-line no-console
        console.warn('Failed to parse creator history from localStorage:', error);
      return [];
    }
  }

  private async removeFromRecentlyVisitedToLocalStorage(
    itemId: TSearchListItem['id'],
  ): Promise<TSearchListItem[]> {
    const currentItems = await this.getRecentlyVisited(true);
    const filteredItems = currentItems.filter((existing) => existing.id !== itemId);
    safeLocalStorage.setItem(
      CreatorHistoryRecentlyVisitedStorageKey,
      JSON.stringify(filteredItems),
    );
    return filteredItems;
  }

  async removeFromRecentlyVisited(itemId: TSearchListItem['id']): Promise<TSearchListItem[]> {
    let localItems = await this.removeFromRecentlyVisitedToLocalStorage(itemId);
    try {
      const items = await this.creatorHistoryApi.removeFromRecentlyVisited(itemId);
      localItems = items; // Use backend result if available
    } catch (error) {
      if (isDevelopment)
        // eslint-disable-next-line no-console
        console.error('Failed to remove from recently visited history:', error);
    }
    return localItems.slice(0, this.config.maxItems) || []; // Fallback to empty array if somehow undefined
  }
}

const creatorHistoryClient = new CreatorHistoryClient();

export default creatorHistoryClient;
